<template>
  <div>
    <div class="flow-block">
      <h4 class="flow-title">1. Identificação da campanha</h4>
      <p class="flow-subtitle">Defina o nome do lote e revise os identificadores sugeridos pelo tenant modelo.</p>

      <div class="vx-row">
        <div class="vx-col w-full md:w-1/2 mb-4">
          <label class="input-label">Nome da Campanha</label>
          <vs-input v-model="form.name" class="w-full" />
        </div>

        <div class="vx-col w-full md:w-1/2 mb-4">
          <label class="input-label">Instância (obrigatório)</label>
          <vs-select
            v-if="hasInstances"
            :value="selectedSuggestions.instanceId"
            class="w-full mb-2"
            placeholder="Selecione a instância"
            @input="onInstanceInput"
          >
            <vs-select-item
              :key="item.id"
              v-for="item in selectorOptions.instances"
              :value="item.id"
              :text="item.label"
            />
          </vs-select>
          <vs-input
            v-else
            v-model="form.instanceName"
            class="w-full"
            placeholder="Digite manualmente o nome da instância"
          />
          <div v-if="selectedInstanceOption" class="text-small mt-1 d-flex" style="gap: 8px;">
            <vs-chip :color="selectedInstanceOption.isOfficial ? 'success' : 'dark'">
              {{ selectedInstanceOption.isOfficial ? 'Conexão Cloud' : 'Conexão web' }}
            </vs-chip>
            <vs-chip :color="selectedInstanceOption.activated ? 'success' : 'danger'">
              {{ selectedInstanceOption.activated ? 'Ativa' : 'Inativa' }}
            </vs-chip>
          </div>
        </div>

        <div class="vx-col w-full md:w-1/2 mb-4">
          <label class="input-label">Grupo de Clientes (obrigatório)</label>
          <vs-select
            v-if="hasCustomerGroups"
            :value="selectedSuggestions.customerGroupId"
            class="w-full mb-2"
            placeholder="Selecione o grupo"
            @input="onCustomerGroupInput"
          >
            <vs-select-item
              :key="item.id"
              v-for="item in selectorOptions.customerGroups"
              :value="item.id"
              :text="item.label"
            />
          </vs-select>
          <vs-input
            v-else
            v-model="form.customerGroupName"
            class="w-full"
            placeholder="Digite manualmente o nome do grupo"
          />
        </div>
      </div>
    </div>

    <div class="flow-block mt-4" v-if="tenantInstanceRows.length">
      <h4 class="flow-title">2. Conexão por dash</h4>
      <p class="flow-subtitle">
        Como os nomes das conexões variam por tenant, confirme a conexão de cada dash.
      </p>

      <div class="vx-row">
        <div class="vx-col w-full md:w-1/2 mb-4" :key="`tenant-instance-${row.tenantId}`" v-for="row in tenantInstanceRows">
          <label class="input-label">{{ row.tenantLabel }}</label>

          <vs-select
            :value="row.selectedInstanceId"
            class="w-full"
            placeholder="Selecione a conexão"
            :disabled="row.isReferenceTenant"
            @input="onTenantInstanceOverrideInput(row.tenantId, $event)"
            v-if="row.optionsCount > 0"
          >
            <vs-select-item
              :key="item.id"
              v-for="item in row.options"
              :value="item.id"
              :text="item.label"
            />
          </vs-select>

          <p v-else-if="loadingTenantInstances && !row.cacheLoaded" class="field-hint mb-0">
            Carregando conexões...
          </p>
          <p v-else class="field-hint text-danger mb-0">
            Nenhuma conexão ativa compatível encontrada.
          </p>

          <p class="field-hint mb-0 mt-1" v-if="row.optionsCount === 1 && row.selectedInstanceName">
            Seleção automática: {{ row.selectedInstanceName }}
          </p>
          <p class="field-hint mb-0 mt-1" v-if="row.isReferenceTenant && row.selectedInstanceName">
            Tenant modelo: usa a mesma conexão selecionada na primeira seção.
          </p>
          <p class="field-hint text-danger mb-0 mt-1" v-if="row.requiredSelection && !row.selectedInstanceName">
            Tenant com múltiplas conexões: seleção manual obrigatória.
          </p>
        </div>
      </div>
    </div>

    <div class="flow-block mt-4" v-if="tenantCustomerGroupRows.length">
      <h4 class="flow-title">3. Grupo de Clientes por dash</h4>
      <p class="flow-subtitle">
        Selecione o grupo de clientes de cada dash. Quando houver apenas 1 opção, a seleção é automática.
      </p>

      <div class="vx-row">
        <div class="vx-col w-full md:w-1/2 mb-4" :key="`tenant-group-${row.tenantId}`" v-for="row in tenantCustomerGroupRows">
          <label class="input-label">{{ row.tenantLabel }}</label>

          <vs-select
            :value="row.selectedCustomerGroupId"
            class="w-full"
            placeholder="Selecione o grupo"
            :disabled="row.isReferenceTenant"
            @input="onTenantCustomerGroupOverrideInput(row.tenantId, $event)"
            v-if="row.optionsCount > 0"
          >
            <vs-select-item
              :key="item.id"
              v-for="item in row.options"
              :value="item.id"
              :text="item.label"
            />
          </vs-select>

          <p v-else-if="loadingTenantInstances && !row.cacheLoaded" class="field-hint mb-0">
            Carregando grupos...
          </p>
          <p v-else class="field-hint text-danger mb-0">
            Nenhum grupo de clientes disponível.
          </p>

          <p class="field-hint mb-0 mt-1" v-if="row.optionsCount === 1 && row.selectedCustomerGroupName">
            Seleção automática: {{ row.selectedCustomerGroupName }}
          </p>
          <p class="field-hint mb-0 mt-1" v-if="row.isReferenceTenant && row.selectedCustomerGroupName">
            Tenant modelo: usa o mesmo grupo selecionado na primeira seção.
          </p>
          <p class="field-hint text-danger mb-0 mt-1" v-if="row.requiredSelection && !row.selectedCustomerGroupName">
            Tenant com múltiplos grupos: seleção manual obrigatória.
          </p>
        </div>
      </div>
    </div>

    <div class="flow-block mt-4" v-if="isTemplateSelectionVisible">
      <h4 class="flow-title">4. Modelo de Mensagem</h4>
      <p class="flow-subtitle">Para conexão Cloud, o modelo é obrigatório.</p>

      <div class="vx-row">
        <div class="vx-col w-full md:w-1/2 mb-4">
          <label class="input-label">Modelo de Mensagem</label>
          <vs-select
            v-if="hasTemplates"
            :value="selectedSuggestions.messageTemplateId"
            class="w-full mb-2"
            placeholder="Selecione o modelo"
            @input="onMessageTemplateInput"
          >
            <vs-select-item
              :key="item.id"
              v-for="item in selectorOptions.messageTemplates"
              :value="item.id"
              :text="item.label"
            />
          </vs-select>
          <p v-else class="field-hint text-danger mb-0">
            Nenhum modelo aprovado foi encontrado. Troque o tenant modelo na etapa 1.
          </p>
        </div>

        <div class="vx-col w-full md:w-1/2 mb-4">
          <label class="input-label">Resumo do modelo</label>
          <div v-if="selectedTemplateOption" class="text-small d-flex flex-wrap" style="gap: 8px;">
            <vs-chip color="primary">Variáveis: {{ selectedTemplateOption.bodyParamsCount }}</vs-chip>
            <vs-chip v-if="selectedTemplateOption.headerFormat" :color="selectedTemplateOption.headerFormat === 'TEXT' ? 'success' : 'warning'">
              Header: {{ selectedTemplateOption.headerFormat }}
            </vs-chip>
            <vs-chip v-if="selectedTemplateOption.cardsCount > 0" color="warning">
              Carrossel: {{ selectedTemplateOption.cardsCount }} cards
            </vs-chip>
          </div>
          <p v-else class="field-hint mb-0">Selecione um modelo para continuar.</p>
        </div>
      </div>

      <div class="template-preview mt-2" v-if="selectedTemplatePreview">
        <div class="preview-section" v-if="selectedTemplatePreview.header">
          <label class="input-label">Cabeçalho</label>
          <div class="preview-box">
            <span v-if="selectedTemplatePreview.header.format === 'TEXT'">{{ selectedTemplatePreview.header.text || '-' }}</span>
            <span v-else>Mídia {{ selectedTemplatePreview.header.format }}</span>
          </div>
        </div>

        <div class="preview-section" v-if="selectedTemplatePreview.body">
          <label class="input-label">Mensagem</label>
          <div class="preview-box">{{ mappedTemplateBodyPreview }}</div>
        </div>

        <div class="preview-section" v-if="selectedTemplatePreview.footer">
          <label class="input-label">Rodapé</label>
          <div class="preview-box">{{ selectedTemplatePreview.footer }}</div>
        </div>

        <div class="preview-section" v-if="selectedTemplatePreview.buttons && selectedTemplatePreview.buttons.length">
          <label class="input-label">Botões</label>
          <div class="d-flex flex-wrap" style="gap: 8px;">
            <vs-chip :key="`template-btn-${index}`" v-for="(button, index) in selectedTemplatePreview.buttons">
              {{ button.text }}
            </vs-chip>
          </div>
        </div>
      </div>

      <div class="mt-4" v-if="templateParamPlaceholders.length > 0">
        <label class="input-label">Mapeamento de variáveis do modelo</label>
        <p class="field-hint mb-2">
          Selecione para cada variável qual campo do grupo de clientes deve ser usado no envio.
        </p>
        <p class="field-hint text-warning mb-2" v-if="referenceGroupHasDiffTagList">
          Atenção: o grupo de referência possui contatos com campos diferentes. Valide se todos os campos mapeados existem para toda a base.
        </p>

        <div class="vx-row">
          <div class="vx-col w-full md:w-1/2 mb-3" :key="`template-param-${placeholder}-${index}`" v-for="(placeholder, index) in templateParamPlaceholders">
            <label class="input-label">Variável {{ formatTemplatePlaceholder(placeholder) }}</label>
            <vs-select
              class="w-full"
              :value="templateParamValues[index] || null"
              placeholder="Selecione o campo do grupo"
              @input="onTemplateParamValueInput(index, $event)"
            >
              <vs-select-item
                :key="`template-tag-${placeholder}-${tag}`"
                v-for="tag in referenceGroupTagOptions"
                :value="tag"
                :text="tag"
              />
            </vs-select>
          </div>
        </div>

        <p class="field-hint text-danger mb-0" v-if="!referenceGroupTagOptions.length">
          O grupo de clientes de referência não possui campos disponíveis para mapear as variáveis.
        </p>
      </div>
    </div>

    <div class="flow-block mt-4">
      <div class="d-flex justify-between items-center flex-wrap" style="gap: 10px;">
        <h4 class="flow-title mb-0">5. Fluxo de conversa</h4>
        <vs-checkbox v-model="form.useChatFlow">Ativar envio por fluxo</vs-checkbox>
      </div>
      <p class="flow-subtitle mb-0">
        Quando ativo, a descrição do fluxo vira obrigatória e substitui o envio por texto simples.
      </p>

      <div class="vx-row mt-3" v-if="form.useChatFlow">
        <div class="vx-col w-full md:w-1/2 mb-4">
          <label class="input-label">Fluxo (obrigatório)</label>
          <vs-select
            v-if="hasChatFlows"
            :value="selectedSuggestions.chatFlowId"
            class="w-full mb-2"
            placeholder="Selecione o fluxo"
            @input="onChatFlowInput"
          >
            <vs-select-item
              :key="item.id"
              v-for="item in selectorOptions.chatFlows"
              :value="item.id"
              :text="item.label"
            />
          </vs-select>
          <vs-input
            v-else
            v-model="form.chatFlowDescription"
            class="w-full"
            placeholder="Digite manualmente a descrição do fluxo"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { formatTemplatePlaceholder } from '@/utils';

export default {
  name: 'MassCampaignStepTwo',
  props: {
    form: {
      type: Object,
      required: true,
    },
    selectorOptions: {
      type: Object,
      required: true,
    },
    selectedSuggestions: {
      type: Object,
      required: true,
    },
    tenantInstanceRows: {
      type: Array,
      required: true,
    },
    tenantCustomerGroupRows: {
      type: Array,
      required: true,
    },
    loadingTenantInstances: {
      type: Boolean,
      required: true,
    },
    selectedTemplatePreview: {
      type: Object,
      default: null,
    },
    templateParamPlaceholders: {
      type: Array,
      required: true,
    },
    templateParamValues: {
      type: Array,
      required: true,
    },
    referenceGroupTagOptions: {
      type: Array,
      required: true,
    },
    referenceGroupHasDiffTagList: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    hasInstances() {
      return Array.isArray(this.selectorOptions.instances) && this.selectorOptions.instances.length > 0;
    },
    hasCustomerGroups() {
      return Array.isArray(this.selectorOptions.customerGroups) && this.selectorOptions.customerGroups.length > 0;
    },
    hasTemplates() {
      return Array.isArray(this.selectorOptions.messageTemplates) && this.selectorOptions.messageTemplates.length > 0;
    },
    hasChatFlows() {
      return Array.isArray(this.selectorOptions.chatFlows) && this.selectorOptions.chatFlows.length > 0;
    },
    selectedInstanceOption() {
      return this.selectorOptions.instances.find((item) => Number(item.id) === Number(this.selectedSuggestions.instanceId)) || null;
    },
    isTemplateSelectionVisible() {
      if (!this.selectedInstanceOption) {
        return false;
      }

      return this.selectedInstanceOption.isOfficial;
    },
    selectedTemplateOption() {
      return this.selectorOptions.messageTemplates.find((item) => Number(item.id) === Number(this.selectedSuggestions.messageTemplateId)) || null;
    },
    mappedTemplateBodyPreview() {
      const body = this.selectedTemplatePreview && this.selectedTemplatePreview.body
        ? `${this.selectedTemplatePreview.body}`
        : '';

      if (!body || !Array.isArray(this.templateParamPlaceholders) || !this.templateParamPlaceholders.length) {
        return body;
      }

      let index = 0;
      return body.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (match, token) => {
        const mappedField = `${this.templateParamValues[index] || ''}`.trim();
        index += 1;
        const normalizedToken = `${token || ''}`.trim() || `${index}`;

        if (!mappedField) {
          return `{{${normalizedToken}: não mapeado}}`;
        }

        return `{{${normalizedToken}: ${mappedField}}}`;
      });
    },
  },
  methods: {
    formatTemplatePlaceholder,
    onInstanceInput(value) {
      this.$emit('update-suggestion', { key: 'instanceId', value });
      this.$emit('apply-instance-suggestion', value);
    },
    onCustomerGroupInput(value) {
      this.$emit('update-suggestion', { key: 'customerGroupId', value });
      this.$emit('apply-customer-group-suggestion', value);
    },
    onMessageTemplateInput(value) {
      this.$emit('update-suggestion', { key: 'messageTemplateId', value });
      this.$emit('apply-message-template-suggestion', value);
    },
    onChatFlowInput(value) {
      this.$emit('update-suggestion', { key: 'chatFlowId', value });
      this.$emit('apply-chat-flow-suggestion', value);
    },
    onTenantInstanceOverrideInput(tenantId, instanceId) {
      this.$emit('set-tenant-instance-override', { tenantId, instanceId });
    },
    onTenantCustomerGroupOverrideInput(tenantId, customerGroupId) {
      this.$emit('set-tenant-customer-group-override', { tenantId, customerGroupId });
    },
    onTemplateParamValueInput(index, value) {
      const current = Array.isArray(this.templateParamValues) ? this.templateParamValues : [];
      const next = [...current];
      next[index] = value;
      this.$emit('update-template-param-values', next);
    }
  }
};
</script>

<style lang="scss" scoped>
.input-label {
  display: block;
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 600;
}

.field-hint {
  font-size: 12px;
  color: #6b7280;
}

.flow-block {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 14px;
  background: #fff;
}

.flow-title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 4px;
}

.flow-subtitle {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 10px;
}

.template-preview {
  border: 1px dashed #d1d5db;
  border-radius: 8px;
  padding: 10px;
}

.preview-section + .preview-section {
  margin-top: 10px;
}

.preview-box {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 10px;
  background: #f9fafb;
  white-space: pre-wrap;
}
</style>
